import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Row, Col, Form, Button } from "react-bootstrap";
import { useAuth } from "../../../context/AuthContext";
import { useAlert } from "../../../context/AlertContext";
import { useLoading } from "../../../context/LoadingContext";
import { getServiceRequest } from "../../../commons/ServiceRequest";
import {
  initializeSetOption,
  initializeSetOptionDisabled
} from "../../../utils/InitializeSetDefault";
import {
  autoDisplayValue,
  formatDateTableForm
} from "../../../commons/FormatValue";
import {
  emptyValueObjects,
  filteredItemInArray,
  findItemInArray
} from "../../../utils/ManageArray";
import {
  FloatingControlComponent,
  SearchSelectComponent
} from "../../FormComponent";
import Table from "../../Table";
import ConfirmModal from "../../ConfirmModal";
import CardDisplayComponent from "../../CardDisplayComponent";
import JobTypeService from "../../../services/jobtype.service";
import CustomersService from "../../../services/customer.service";
import CusBranchsService from "../../../services/cusbranch.service";
import CustContractItemsService from "../../../services/custcontractitem.service";
import PMPlansService from "../../../services/pmplan.service";

const CreatePlanPMForm = ({ onSubmitCallback }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const { setLoading } = useLoading();
  const [showModal, setShowModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [pmPlans, setPMPlans] = useState([]);
  const [jobType, setJobType] = useState([]);
  const [contract, setContract] = useState([]);
  const [cusBranch, setCusBranch] = useState([]);
  const defaultData = {
    Contract_Id: "",
    Contract_No: "",
    Cus_Id: "",
    CustContractCus_Id: "",
    Job_Type_Id: 5,
    Item_Id: "",
    Plan_No: "",
    Plan_Date: formatDateTableForm(new Date()),
    Plan_Status: "OPEN",
    Com_Code: user.Com_Code
  };
  const [data, setData] = useState({ Array: [defaultData] });
  const [dataTable, setDataTable] = useState({ Array: [defaultData] });
  const rowData = dataTable.Array?.[0];
  const columns = [
    {
      name: "ลำดับ",
      selector: (row) => row?.rowIndex,
      sortable: true,
      width: "110px"
    },
    {
      name: "รหัสสาขา",
      selector: (row) => row?.Customer?.Cus_Code,
      minWidth: "200px"
    },
    {
      name: "เลขที่สาขา",
      selector: (row) => row?.Customer?.Cus_NumBranch,
      minWidth: "150px"
    },
    {
      name: "ชื่อสาขา",
      selector: (row) => row?.Customer?.Cus_Name,
      minWidth: "300px"
    },
    {
      name: "รหัสสินค้า",
      selector: (row) => row?.Item?.Item_Code,
      minWidth: "200px"
    },
    {
      name: "รายละเอียดสินค้า",
      selector: (row) => row?.Item?.Item_Name_TH,
      minWidth: "300px"
    },
    {
      name: "หมายเลขเครื่อง (Serial No.)",
      selector: (row) => row?.Item?.Item_PartNo,
      minWidth: "300px"
    },
    {
      name: "จำนวน PM/ปี",
      selector: (row) => row?.CustContract?.PM_Per_Year,
      minWidth: "150px"
    }
  ];
  const inputField = [
    {
      name: "Contract_No",
      label: "เลขที่สัญญา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "select",
      options: initializeSetOptionDisabled(
        { key: "Contract_No", dataset: contract },
        { key: "CustContractItem.Contract_No", data: pmPlans }
      ),
      queryService: (query) =>
        getServiceRequest({
          showAlert,
          setLoading,
          serviceRequest:
            CustContractItemsService.getAllCustContractItems(query),
          callback: ({ items }) => setContract(items)
        })
    },
    {
      name: "Contract_Type",
      label: "ประเภทสัญญา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "text",
      disabled: true,
      findValue: { data: contract, key: "Id", value: "Contract_Id" }
    },
    {
      name: "CustContract.Project_No",
      label: "เลขที่โครงการ",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "text",
      disabled: true,
      findValue: { data: contract, key: "Id", value: "Contract_Id" }
    },
    {
      name: "Plan_No",
      label: "หมายเลขแผน PM",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "text",
      disabled: true
    },
    {
      name: "createdBy",
      label: "ผู้สร้างแผน",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "text",
      disabled: true
    },
    {
      name: "Plan_Date",
      label: "วันที่สร้างแผน",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "date",
      disabled: true
    },
    {
      name: "Job_Type_Id",
      label: "ประเภทงาน",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "select",
      filterBy: ["Job_Type_Name"],
      noneAsync: true,
      options: initializeSetOption({
        key: "Id",
        label: "Job_Type_Name",
        dataset: jobType
      })
    },
    {
      name: "Plan_Status",
      label: "สถานะแผน",
      classNames: { col: "mb-3", label: "mb-1" },
      type: "select",
      noneAsync: true,
      options: [
        { value: "OPEN", label: "OPEN" },
        { value: "CANCEL", label: "CANCEL" },
        { value: "CLOSED", label: "CLOSED" }
      ]
    },
    {
      name: "CustContractCus_Id",
      label: "รหัสลูกค้า",
      type: "select",
      noneAsync: rowData?.Contract_Id !== "",
      options: initializeSetOption({
        key: "CustContract.Cus_Id",
        label: "CustContract.Customer.Cus_Code",
        dataset: filteredItemInArray(contract, "Id", rowData?.Contract_Id)
      })
    },
    {
      name: "CustContractCus_Id",
      label: "ชื่อลูกค้า",
      type: "select",
      size: 9,
      noneAsync: rowData?.Contract_Id !== "",
      options: initializeSetOption({
        key: "CustContract.Cus_Id",
        label: "CustContract.Customer.Cus_Name",
        dataset: filteredItemInArray(contract, "Id", rowData?.Contract_Id)
      })
    }
  ];

  const floatingSearch = ({
    name,
    label,
    classNames,
    options,
    filterBy,
    noneAsync,
    queryService
  }) => {
    return (
      <SearchSelectComponent
        labelField={label}
        floatingClass={classNames?.label}
        filterBy={filterBy}
        options={options}
        placeholder="โปรดเลือก"
        noneAsync={noneAsync}
        queryService={queryService}
        isInvalid={rowData?.[name] === ""}
        onChange={(change) => handleChange({ name, label, change })}
        value={`${rowData?.[name]}`}
      />
    );
  };

  const floatingInput = ({ name, label, type, classNames, findValue }) => {
    let result;
    if (findValue) {
      result = autoDisplayValue({
        name,
        type,
        findValue: { ...findValue, value: rowData?.[findValue?.value] }
      });
    } else result = rowData?.[name];

    return (
      <FloatingControlComponent
        disabled
        type={type}
        name={name}
        label={label}
        className={classNames}
        placeholder="โปรดระบุ"
        value={result}
      />
    );
  };

  const commonUpdatedData = (data) => {
    return data.map(
      ({
        rowIndex,
        CustContract,
        Customer,
        Item,
        Contract_No,
        CustContractCus_Id,
        ...item
      }) => item
    );
  };

  const handleChange = ({ name, change }) => {
    let updatedData = dataTable.Array;

    if (name === "Contract_No") {
      const ContractItem = filteredItemInArray(
        contract,
        "Contract_No",
        change?.value
      );
      if (ContractItem?.length > 0) {
        updatedData = ContractItem.map(
          ({
            Id,
            Contract_No,
            CustContract,
            Item,
            Item_Id,
            Com_Code,
            ...item
          }) => ({
            Contract_Id: Id,
            Contract_No,
            CustContract: item,
            Item_Id,
            Item,
            Cus_Id: CustContract?.Cus_Id,
            Customer: CustContract?.Customer,
            Job_Type_Id: rowData?.Job_Type_Id,
            Plan_No: rowData?.Plan_No,
            Plan_Date: rowData?.Plan_Date,
            Plan_Status: rowData?.Plan_Status,
            Com_Code
          })
        );
      } else {
        updatedData = [defaultData];
      }
    } else if (name === "CustContractCus_Id" && change) {
      getServiceRequest({
        showAlert,
        setLoading,
        serviceRequest: CustomersService.getCustomerBranches(change?.value),
        callback: (res) => {
          const { Contract_No } = findItemInArray(
            contract,
            "Id",
            rowData?.Contract_Id
          );
          if (res?.length > 0) {
            updatedData = updatedData.flatMap((item) =>
              res
                .filter((b) =>
                  cusBranch.some(
                    (cb) =>
                      cb?.Contract_No === Contract_No && b?.Id === cb?.Cus_Id
                  )
                )
                .map((branch) => ({
                  ...item,
                  Cus_Id: branch?.Id,
                  Customer: branch
                }))
            );
          }
          updatedData = updatedData.map((item) => ({
            ...item,
            CustContractCus_Id: change?.value
          }));
          setDataTable({ Array: updatedData });
        }
      });
    } else {
      updatedData = updatedData.map((item) => ({
        ...item,
        [name]: change?.value
      }));
    }

    setDataTable({ Array: updatedData });
    if (data.Array.length > 0) setData({ Array: updatedData });
  };

  const checkValidity = () => {
    if (data.Array?.length === 0) {
      showAlert({
        error: "ข้อผิดพลาด",
        message: "กรุณาเลือกข้อมูลเพื่อสร้างแผน PM",
        type: "danger"
      });
      return false;
    }
    if (emptyValueObjects(data.Array, ["Plan_No"]).length > 0) {
      showAlert({
        error: "ข้อผิดพลาด",
        message: "กรุณากรอกข้อมูลให้ครบถ้วน",
        type: "danger"
      });
      return false;
    }
    return true;
  };

  const handleValidated = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity() || !checkValidity()) {
      event.stopPropagation();
      setValidated(true);
    } else {
      setShowModal(!showModal);
    }
  };

  const handleSubmit = (event, { dataCallback, showCallback }) => {
    event.preventDefault();
    setShowModal(showCallback);

    if (dataCallback)
      onSubmitCallback({ Array: commonUpdatedData(dataCallback?.Array) });
  };

  useEffect(() => {
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: JobTypeService.getAllJobTypes(),
      callback: (res) => setJobType(res)
    });
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: CusBranchsService.getAllCusBranchs(),
      callback: (res) => setCusBranch(res)
    });
    getServiceRequest({
      showAlert,
      setLoading,
      serviceRequest: PMPlansService.getAllPMPlans(),
      callback: (res) => setPMPlans(res)
    });
  }, []);

  return (
    <>
      <Form noValidate validated={validated} onSubmit={handleValidated}>
        <CardDisplayComponent
          header={{ title: { text: "สร้างแผน PM", className: "text-center" } }}
          body={{
            as: Row,
            element: inputField.map((item, idx) => (
              <Col
                key={`${item.name}-${idx}`}
                sm={item.size ?? (item.size === null ? null : 3)}
                className={item.classNames?.col}
              >
                {item.type === "select"
                  ? floatingSearch(item)
                  : floatingInput(item)}
              </Col>
            ))
          }}
        />

        <CardDisplayComponent
          header={{ title: { text: "รายละเอียดสินค้า" } }}
          body={{
            element: (
              <Table
                data={dataTable?.Array}
                column={columns}
                selectableRows
                onSelectedRowsChange={({ selectedRows }) =>
                  setData({ Array: commonUpdatedData(selectedRows) })
                }
              />
            )
          }}
          footer={{
            className: "text-end",
            element: (
              <>
                <Button
                  type="submit"
                  variant="success"
                  className="me-2 px-5 py-2 text-dark rounded-pill"
                >
                  บันทึกข้อมูล
                </Button>
                <ConfirmModal
                  show={showModal}
                  data={data}
                  content="save"
                  onSubmitCallback={handleSubmit}
                />

                <Button
                  variant="outline-secondary"
                  className="px-5 py-2 text-dark border-dark border-opacity-50 rounded-pill"
                  onClick={() => navigate("/PM_work/create", { state })}
                >
                  ยกเลิก
                </Button>
              </>
            )
          }}
        />
      </Form>
    </>
  );
};

CreatePlanPMForm.propTypes = { onSubmitCallback: PropTypes.func };

export default CreatePlanPMForm;
